import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles, type Article } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article: Article | undefined = articles.find((a) => a.id === Number(id));

  const relatedArticles = articles
    .filter((a) => a.id !== Number(id) && a.category === article?.category)
    .slice(0, 3);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, [id]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-24 pb-14 min-h-screen bg-background-50 flex items-center justify-center">
          <div className="text-center">
            <i className="ri-file-unknow-line text-5xl text-foreground-300 mb-4 block"></i>
            <h1 className="text-xl font-semibold text-foreground-700 mb-2">文章不存在</h1>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: article.image,
  };

  return (
    <>
      <Navbar />
      <main className="pt-20 pb-14 min-h-screen bg-background-50">
        <article className="w-full px-4 md:px-6">
          <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>

          <div className="max-w-3xl mx-auto">
            <div className="mb-4">
              <Breadcrumb
                items={[
                  { label: '首页', path: '/' },
                  { label: article.category, path: '/category/football' },
                  { label: article.title },
                ]}
              />
            </div>

            <div className="mb-6">
              <span className="px-3 py-1 bg-primary-100 text-primary-600 text-xs font-medium rounded-full">
                {article.category}
              </span>
            </div>

            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 text-sm text-foreground-500 mb-6">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                {article.readTime}
              </span>
            </div>

            <div className="rounded-lg overflow-hidden mb-8 h-[300px] md:h-[400px]">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-8 pt-6 border-t border-background-200/70">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full border border-background-200/60"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {relatedArticles.length > 0 && (
              <div className="mt-12">
                <h3 className="text-lg font-heading font-bold text-foreground-900 mb-5">相关文章</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.id}`}
                      className="group bg-background-50 border border-background-200/80 rounded-lg overflow-hidden hover:border-primary-300 transition-all duration-200 cursor-pointer"
                    >
                      <div className="h-36 overflow-hidden">
                        <img
                          src={ra.image}
                          alt={ra.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-3">
                        <h4 className="text-sm font-semibold text-foreground-700 line-clamp-2 group-hover:text-primary-500 transition-colors">
                          {ra.title}
                        </h4>
                        <p className="text-xs text-foreground-400 mt-1">{ra.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}